/**
 * DisclosureFilters Component
 *
 * Filter bar with:
 * - Search input (borrower, loan #, address)
 * - Lock Status dropdown
 * - ITOP Status dropdown
 */

import React from 'react';
import { Search, X, RotateCcw, Send, AlertTriangle, Clock, CheckCircle2, FileCheck, AlertOctagon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import type { DisclosureFilters as DisclosureFiltersType } from '@/types/disclosure-dashboard';

interface DisclosureFiltersProps {
  filters: DisclosureFiltersType;
  onFilterChange: <K extends keyof DisclosureFiltersType>(key: K, value: DisclosureFiltersType[K]) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

export function DisclosureFilters({
  filters,
  onFilterChange,
  onReset,
  hasActiveFilters
}: DisclosureFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Search */}
      <div className="relative flex-1 min-w-[200px] max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search borrower, loan #, address..."
          value={filters.search}
          onChange={e => onFilterChange('search', e.target.value)}
          className="pl-9 h-9"
        />
        {filters.search && (
          <button
            onClick={() => onFilterChange('search', '')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Disclosure Status */}
      <Select
        value={filters.disclosureStatus}
        onValueChange={value => onFilterChange('disclosureStatus', value as DisclosureFiltersType['disclosureStatus'])}
      >
        <SelectTrigger className="w-[165px] h-9">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectSeparator />
          <SelectItem value="le_overdue">
            <span className="flex items-center gap-2">
              <AlertOctagon className="h-3 w-3 text-red-700" />
              <span className="text-red-700 font-medium">LE Overdue</span>
            </span>
          </SelectItem>
          <SelectSeparator />
          <SelectItem value="not_sent">
            <span className="flex items-center gap-2">
              <Send className="h-3 w-3 text-red-600" />
              Send LE
            </span>
          </SelectItem>
          <SelectItem value="itop_missing">
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-3 w-3 text-orange-600" />
              ITOP Missing
            </span>
          </SelectItem>
          <SelectItem value="itop_received">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-3 w-3 text-blue-600" />
              ITOP ✓
            </span>
          </SelectItem>
          <SelectItem value="cd_sent">
            <span className="flex items-center gap-2">
              <Clock className="h-3 w-3 text-purple-600" />
              CD Pending
            </span>
          </SelectItem>
          <SelectItem value="complete">
            <span className="flex items-center gap-2">
              <FileCheck className="h-3 w-3 text-green-600" />
              Complete
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      {/* Lock Status */}
      <Select
        value={filters.lockStatus}
        onValueChange={value => onFilterChange('lockStatus', value as DisclosureFiltersType['lockStatus'])}
      >
        <SelectTrigger className="w-[140px] h-9">
          <SelectValue placeholder="Lock Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Locks</SelectItem>
          <SelectItem value="Locked">Locked</SelectItem>
          <SelectItem value="NotLocked">Not Locked</SelectItem>
          <SelectItem value="Expired">Expired</SelectItem>
        </SelectContent>
      </Select>

      {/* ITOP Status */}
      <Select
        value={filters.itopStatus}
        onValueChange={value => onFilterChange('itopStatus', value as DisclosureFiltersType['itopStatus'])}
      >
        <SelectTrigger className="w-[150px] h-9">
          <SelectValue placeholder="ITOP Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All ITOP</SelectItem>
          <SelectItem value="missing">ITOP Missing</SelectItem>
          <SelectItem value="received">ITOP Received</SelectItem>
          <SelectItem value="not_applicable">Not Sent Yet</SelectItem>
        </SelectContent>
      </Select>

      {/* Reset Filters */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="h-9 text-muted-foreground hover:text-foreground"
        >
          <RotateCcw className="h-4 w-4 mr-1" />
          Reset
        </Button>
      )}
    </div>
  );
}
